/**
 * StoryCard component - Displays a story with carousel navigation in the feed
 */

import { useState, useEffect } from 'react';
import { Heart, MessageCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '../ui/carousel';
import type { Story } from '../../types';
import { cn } from '../../lib/utils';
import { useLikeStory, useUnlikeStory } from '../../services/stories';
import { VideoPlayer } from '../video/VideoPlayer';

interface StoryCardProps {
  story: Story;
  onStoryClick?: (storyId: string) => void;
  onUserClick?: (userId: string) => void;
}

/**
 * Format time ago (e.g., "2 hours ago")
 */
const formatTimeAgo = (timestamp: string): string => {
  const now = new Date();
  const uploaded = new Date(timestamp);
  const seconds = Math.floor((now.getTime() - uploaded.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return uploaded.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Get user initials for avatar fallback
 */
const getUserInitials = (displayName: string): string => {
  return displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const StoryCard = ({ story, onStoryClick, onUserClick }: StoryCardProps) => {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const likeMutation = useLikeStory();
  const unlikeMutation = useUnlikeStory();

  const isLiked = story.isLikedByCurrentUser || false;
  const showNavigation = story.photoCount > 1;

  // Update carousel state
  useEffect(() => {
    if (!carouselApi) return;

    const updateCarouselState = () => {
      setCurrentIndex(carouselApi.selectedScrollSnap());
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
    };

    updateCarouselState();
    carouselApi.on('select', updateCarouselState);

    return () => {
      carouselApi.off('select', updateCarouselState);
    };
  }, [carouselApi]);

  const handleLikeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLiked) {
      unlikeMutation.mutate(story.storyId);
    } else {
      likeMutation.mutate(story.storyId);
    }
  };

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onUserClick && story.user) {
      onUserClick(story.user.userId);
    }
  };

  const handleStoryClick = () => {
    if (onStoryClick) {
      onStoryClick(story.storyId);
    }
  };

  const handlePrevClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    carouselApi?.scrollPrev();
  };

  const handleNextClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    carouselApi?.scrollNext();
  };

  return (
    <Card className="overflow-hidden">
      {/* Header - User info */}
      <div className="flex items-center justify-between p-4">
        <button
          onClick={handleUserClick}
          className="flex items-center gap-3 text-left transition-opacity hover:opacity-80"
        >
          <Avatar>
            <AvatarImage src={story.user?.avatarUrl} alt={story.user?.displayName} />
            <AvatarFallback>{getUserInitials(story.user?.displayName || 'U')}</AvatarFallback>
          </Avatar>
          <div>
            <p className="text-sm font-semibold">{story.user?.displayName}</p>
            <p className="text-xs text-muted-foreground">{formatTimeAgo(story.uploadedAt)}</p>
          </div>
        </button>
      </div>

      {/* Photo carousel */}
      <div className="relative">
        <Carousel setApi={setCarouselApi} opts={{ loop: true }} className="w-full">
          <CarouselContent>
            {story.photos.map((photo) => (
              <CarouselItem key={photo.photoId}>
                <div
                  className="relative aspect-square w-full overflow-hidden bg-muted"
                  onClick={photo.mediaType === 'image' ? handleStoryClick : undefined}
                >
                  {photo.mediaType === 'video' && photo.videoUrl ? (
                    <VideoPlayer
                      src={photo.videoUrl}
                      poster={photo.images.thumbnail}
                      className="aspect-square w-full"
                      autoPlay={false}
                      loop={false}
                      muted={true}
                    />
                  ) : (
                    <img
                      src={photo.images.medium}
                      alt={story.caption || 'Story photo'}
                      className="h-full w-full cursor-pointer object-cover"
                      loading="lazy"
                    />
                  )}
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>

          {/* Navigation arrows - only show if more than one photo */}
          {showNavigation && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'absolute left-2 top-1/2 z-10 h-10 w-10 -translate-y-1/2 rounded-full bg-black/50 text-white transition-opacity hover:bg-black/70 hover:text-white',
                  !canScrollPrev && 'hidden'
                )}
                onClick={handlePrevClick}
                aria-label="Previous photo"
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'absolute right-2 top-1/2 z-10 h-10 w-10 -translate-y-1/2 rounded-full bg-black/50 text-white transition-opacity hover:bg-black/70 hover:text-white',
                  !canScrollNext && 'hidden'
                )}
                onClick={handleNextClick}
                aria-label="Next photo"
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </>
          )}
        </Carousel>

        {/* Dot indicators - only show if more than one photo */}
        {showNavigation && (
          <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
            {story.photos.map((_, index) => (
              <div
                key={index}
                className={cn(
                  'h-1.5 w-1.5 rounded-full transition-all',
                  index === currentIndex ? 'w-4 bg-white' : 'bg-white/60'
                )}
              />
            ))}
          </div>
        )}
      </div>

      {/* Actions and caption */}
      <div className="space-y-3 p-4">
        {/* Action buttons */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            className="h-auto gap-2 p-0 hover:bg-transparent"
            onClick={handleLikeClick}
          >
            <Heart
              className={cn('h-6 w-6 transition-colors', isLiked && 'fill-red-500 text-red-500')}
            />
            {story.likeCount > 0 && (
              <span className="text-sm font-semibold">{story.likeCount}</span>
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto gap-2 p-0 hover:bg-transparent"
            onClick={handleStoryClick}
          >
            <MessageCircle className="h-6 w-6" />
            {story.commentCount > 0 && (
              <span className="text-sm font-semibold">{story.commentCount}</span>
            )}
          </Button>
        </div>

        {/* Caption */}
        {story.caption && (
          <div>
            <button
              onClick={handleUserClick}
              className="mr-2 text-sm font-semibold transition-opacity hover:opacity-80"
            >
              {story.user?.displayName}
            </button>
            <span className="text-sm">{story.caption}</span>
          </div>
        )}

        {/* View comments link */}
        {story.commentCount > 0 && (
          <button
            onClick={handleStoryClick}
            className="text-sm text-muted-foreground transition-opacity hover:opacity-80"
          >
            View all {story.commentCount} comment{story.commentCount !== 1 ? 's' : ''}
          </button>
        )}
      </div>
    </Card>
  );
};
